import {GridColDef} from '@mui/x-data-grid';
import {DEFAULT_SORT_FIELD_EMPLOYER_SUMMARY, getEmployerSummary} from 'Agent/Commissions/ducks/a360Commissions';
import React from 'react';
import {A360CommissionsGrid} from './CommissionsGrid';
import {A360EmployerSummary} from '../types';
import {useDatagridState} from './useDatagridState';
import {A360CommissionSection} from '../CommissionSection';
import {currencyValueFormatter, extractProducerCodes, revertNoEmployerToEmpty} from '../utilities';

const employerSummaryColumns: GridColDef[] = [
  {field: 'statementDate', headerName: 'Commission Statement Date', width: 200},
  {field: 'entryType', headerName: 'Type', width: 100},
  {field: 'employerId', headerName: 'Employer #', width: 180},
  {field: 'employerName', headerName: 'Employer Name', width: 180},
  {field: 'billingAccountId', headerName: 'Billing Account #', width: 190},
  {field: 'billingAccountType', headerName: 'Billing Account Type', width: 200},
  {field: 'masterPolicyId', headerName: 'Master Policy #', width: 180},
  {field: 'productName', headerName: 'Product Name', width: 180},
  {field: 'productCode', headerName: 'Product Code', width: 150},
  {field: 'periodEndingDate', headerName: 'Commission Period End Date', width: 180},
  {
    field: 'premiumProcessed',
    headerName: 'Premium Processed',
    width: 150,
    valueFormatter: currencyValueFormatter,
  },
  {
    field: 'annualizedPremium',
    headerName: 'Annualized Premium',
    width: 150,
    valueFormatter: currencyValueFormatter,
  },
  {
    field: 'commissionPayable',
    headerName: 'Net Commission Payable',
    width: 200,
    valueFormatter: currencyValueFormatter,
  },
  {
    field: 'commissionGenerated',
    headerName: 'Commission Generated',
    width: 200,
    valueFormatter: currencyValueFormatter,
  },
  {
    field: 'accountAdvanceRecovery',
    headerName: 'Account Advance Recovery',
    width: 200,
    valueFormatter: currencyValueFormatter,
  },
  {field: 'commissionStyle', headerName: 'Commission Style', width: 150},
].map(col => ({...col, filterable: false, headerClassName: 'a360-commissions-grid__header'}));

const getEmployerSummaryRowId = (row: A360EmployerSummary) =>
  row?.commissionStatementGroupedId ??
  `${row.annualizedPremium}-${row.billingAccountId}-${row.billingAccountType}-${row.commissionPayable}-
        ${row.employerId}-${row.employerName}-${row.entryType}-${row.masterPolicyId}-${row.periodEndingDate}-${row.premiumProcessed}-
        ${row.productCode}-${row.productName}-${row.statementDate}-${row.commissionGenerated}-${row.accountAdvanceRecovery}-${row.commissionStyle}`;

export const EmployerSummary = () => {
  const {filter, rows, error, loading, totalCount, handleFetch} = useDatagridState<A360EmployerSummary>(
    'employerSummary',
    getEmployerSummary,
    DEFAULT_SORT_FIELD_EMPLOYER_SUMMARY
  );

  return (
    <A360CommissionSection className="a360-commissions__employer-summary" aria-label="employer summary">
      <A360CommissionsGrid<A360EmployerSummary>
        columns={employerSummaryColumns}
        error={error}
        getRowId={getEmployerSummaryRowId}
        rows={rows}
        loading={loading}
        totalRecords={totalCount}
        onFetch={handleFetch}
        initialSort={[{field: DEFAULT_SORT_FIELD_EMPLOYER_SUMMARY, sort: 'desc'}]}
        title="Employer Summary"
        toolbarProps={{
          exportToCsv: {
            requestPayload: {
              agencyCodes: extractProducerCodes(filter.selectedProducerCodes),
              statementDates: filter.selectedStatementDates,
              employerNames: revertNoEmployerToEmpty(filter.selectedEmployerNames),
              sortFields: [DEFAULT_SORT_FIELD_EMPLOYER_SUMMARY],
            },
            downloadedFileName: 'EmployerSummary',
            relativeFileLocation: '/api/commissions/v1/commissionStatements/employerSummary/csv',
          },
        }}
      />
    </A360CommissionSection>
  );
};
